import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileMedical, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import API_URL from '../config';

const AnalysisList = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecordings();
  }, []);

  const fetchRecordings = async () => {
    try {
      // Re-using the recent endpoint which is ordered by last_opened
      const response = await fetch(`${API_URL}/api/recording/recent/`);
      if (response.ok) {
        const data = await response.json();
        setRecordings(data);
      }
    } catch (e) {
      console.error("Failed to fetch recordings", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecording = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;
    try {
      const response = await fetch(`${API_URL}/api/recording/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        setRecordings(recordings.filter(r => r.id !== id));
      } else {
        alert('Failed to delete recording');
      }
    } catch (err) {
      alert('Error deleting recording');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full pt-20">
        <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-4xl" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Your Recordings</h1>
      {recordings.length === 0 ? (
        <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No recordings found. Go to the Home page to upload an EDF file or try the Demo.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {recordings.map(rec => (
              <Link
                key={rec.id}
                to={`/analysis/${rec.id}/report`}
                className="flex flex-col sm:flex-row sm:items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 cursor-pointer group"
                style={{ textDecoration: 'none' }}
              >
                <div className="flex items-center mb-2 sm:mb-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                    <FontAwesomeIcon icon={faFileMedical} className="text-blue-500 dark:text-blue-400 text-lg" />
                  </div>
                </div>
                <div className="sm:ml-4 flex-grow">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      Study for {rec.patient_name || `Patient #${rec.patient}`}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">
                      Last Opened: {new Date(rec.last_opened).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    Recorded on {rec.recording_date} &bull; Signal Quality: {rec.signal_quality} &bull; Duration: {rec.total_recording_time} mins
                  </p>
                </div>
                <button
                  className="ml-4 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors duration-200"
                  onClick={e => { e.preventDefault(); e.stopPropagation(); handleDeleteRecording(rec.id); }}
                  title="Delete recording"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisList;
